import { cn } from "@/lib/utils";
import { Bot, User, BookOpen, ThumbsUp, ThumbsDown } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import type { Components } from "react-markdown";
import type { ChatMessage } from "@/types/chat";
import { SourcesAccordion } from "./SourcesAccordion";

interface ChatMessageBubbleProps {
  message: ChatMessage;
  isReadOnly?: boolean;
  personaLabel?: string;
  onFeedback?: (messageId: number, feedback: 1 | -1 | null) => void;
}

export function ChatMessageBubble({ message, isReadOnly, personaLabel, onFeedback }: ChatMessageBubbleProps) {
  const isUser = message.role === "user";
  const navigate = useNavigate();
  const time = new Date(message.created_at).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const markdownComponents: Components = {
    a: ({ href, children, ...props }) => {
      const bookMatch = href?.match(/^\/book\/(\d+)$/);
      if (bookMatch) {
        return (
          <button
            type="button"
            onClick={() => navigate(`/admin/books/${bookMatch[1]}`)}
            className="inline-flex items-center gap-1 text-primary underline font-medium hover:text-primary/80"
          >
            <BookOpen className="w-3 h-3" />
            {children}
          </button>
        );
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    },
  };

  return (
    <div
      className={cn(
        "flex gap-3 max-w-[85%]",
        isUser ? "ml-auto flex-row-reverse" : "mr-auto"
      )}
    >
      <div
        className={cn(
          "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground"
        )}
      >
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>

      <div className={cn("flex flex-col gap-1", isUser ? "items-end" : "items-start")}>
        {!isUser && personaLabel && (
          <span className="text-[10px] font-medium text-primary/70 px-1">{personaLabel}</span>
        )}
        <div
          className={cn(
            "rounded-2xl px-4 py-2.5 text-sm",
            isUser
              ? "bg-primary text-primary-foreground rounded-br-md whitespace-pre-wrap"
              : "bg-muted text-foreground rounded-bl-md"
          )}
        >
          {isUser ? (
            message.content
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-headings:text-sm prose-p:leading-relaxed">
              <ReactMarkdown components={markdownComponents}>{message.content}</ReactMarkdown>
            </div>
          )}
        </div>

        {!isUser && message.sources && message.sources.length > 0 &&
          "score" in message.sources[0] && (
          <SourcesAccordion sources={message.sources} />
        )}

        {!isUser && (
          <div className="flex items-center gap-1 px-1">
            {onFeedback ? (
              <>
                <button
                  type="button"
                  onClick={() => onFeedback(message.id, message.feedback === 1 ? null : 1)}
                  className={cn(
                    "p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors",
                    message.feedback === 1
                      ? "text-green-600"
                      : "text-muted-foreground/50 hover:text-green-600"
                  )}
                  title="Good response"
                >
                  <ThumbsUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => onFeedback(message.id, message.feedback === -1 ? null : -1)}
                  className={cn(
                    "p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors",
                    message.feedback === -1
                      ? "text-red-500"
                      : "text-muted-foreground/50 hover:text-red-500"
                  )}
                  title="Bad response"
                >
                  <ThumbsDown className="w-3.5 h-3.5" />
                </button>
              </>
            ) : message.feedback ? (
              <span className={cn(
                "flex items-center gap-0.5 text-[10px]",
                message.feedback === 1 ? "text-green-600" : "text-red-500"
              )}>
                {message.feedback === 1 ? <ThumbsUp className="w-3 h-3" /> : <ThumbsDown className="w-3 h-3" />}
              </span>
            ) : null}
          </div>
        )}

        <span className="text-[10px] text-muted-foreground px-1">{time}</span>
      </div>
    </div>
  );
}
